import type {
  DataTag,
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { keepPreviousData, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { BodyType, ErrorType } from '../axios';
import { request } from '../axios';
import type { PreventiveTherapyDto } from '../generated/contact-tracing-controller/preventive-therapy-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  LocaleDto,
  PatientDtoGender,
  PatientDtoResidencyPermit,
  TuberculosisHistoryDto,
  UserDto,
} from '../generated/model';
import type { PersonLivingStatus } from '../generated/person-controller/person-controller';

type RequestOptions = Parameters<typeof request>[1];

export enum ScreeningClientType {
  BAZ_ZURICH = 'BAZ_ZURICH',
  SELF_REFERRAL = 'SELF_REFERRAL',
  OTHER = 'OTHER',
}

export enum ScreeningDesiredTest {
  MX = 'MX',
  QFT = 'QFT',
  RX = 'RX',
}

export enum ScreeningStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export interface CreateScreeningCaseDto {
  screeningAssignment: string | null;
  clientType: ScreeningClientType;
  institution: AddressDto | null;
  contactPerson: AddressContactDto | null;
  groupName: string | null;
  desiredTest: ScreeningDesiredTest | null;
  testDate: string | null;
  assignedEmployeeId: string | null;
}

export interface UpdateScreeningCaseDto {
  screeningAssignment: string | null;
  clientType: ScreeningClientType;
  institution: AddressDto | null;
  contactPerson: AddressContactDto | null;
  groupName: string;
  desiredTest: ScreeningDesiredTest | null;
  testDate: string | null;
  status: ScreeningStatus;
  assignedEmployeeId: string | null;
}

export interface ScreeningCaseDto {
  id: string;
  screeningAssignment: string;
  clientType: ScreeningClientType;
  institution: AddressDto | null;
  contactPerson: AddressContactDto | null;
  groupName: string;
  desiredTest: ScreeningDesiredTest | null;
  testDate: string | null;
  status: ScreeningStatus;
  closedAt: string | null;
  canClose?: boolean;
  assignedEmployee: UserDto | null;
  created: string;
  createdBy: UserDto | null;
  updated: string;
  updatedBy: UserDto | null;
}

export interface ScreeningTracingReferencePersonDto {
  id: string;
  relationType: string;
  salutation: string;
  firstName: string;
  lastName: string;
  position: string;
  phoneNumber: string;
  email: string;
  isSelected: boolean;
}

export interface ScreeningTracingIndividualDetailsDto {
  id: string;
  caseType: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: PatientDtoGender | null;
  salutation: string | null;
  livingStatus: PersonLivingStatus | null;
  note: string | null;
  phone: string;
  email: string;
  mobilePhoneNumber: string;
  privatePhoneNumber: string;
  workPhoneNumber: string;
  nationality: LocaleDto | null;
  origin: LocaleDto | null;
  arrival: string;
  asylumNumber: string;
  zemisNumber: string;
  residencyPermit: PatientDtoResidencyPermit | null;
  street: string;
  postalCode: number | string;
  city: string;
  state: string;
  entryDate: string;
  additionalInformation: string;
  languages: LocaleDto[];
  bezugspersonen: ScreeningTracingReferencePersonDto[];
  personId: string;
  created: string;
  createdBy: UserDto | null;
  contactPatientId: string;
  contactPatientCaseNumber: string;
  contactPatientRelationshipStatus: string;
  waitingPeriodEnd: string;
  assessmentStatus: string;
  assessmentNotes: string;
  screeningCaseId: string;
  screeningCase: ScreeningCaseDto | null;
}

export interface UpdateScreeningTracingIndividualDto {
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: PatientDtoGender | null;
  salutation: string | null;
  livingStatus?: PersonLivingStatus | null;
  note?: string | null;
  email: string | null;
  mobilePhoneNumber: string | null;
  privatePhoneNumber: string | null;
  workPhoneNumber: string | null;
  nationality: LocaleDto | null;
  origin: LocaleDto | null;
  arrival: string | null;
  asylumNumber: string | null;
  zemisNumber: string | null;
  residencyPermit: PatientDtoResidencyPermit | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  state: string | null;
  additionalInformation: string | null;
  languages: LocaleDto[];
  bezugspersonIds: string[];
}

export const createScreeningCase = (
  createScreeningCaseDto: BodyType<CreateScreeningCaseDto>,
  options?: RequestOptions,
) => {
  return request<string>(
    {
      url: `/api/screening-cases`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createScreeningCaseDto,
    },
    options,
  );
};

export const getScreeningCaseById = (id: string, options?: RequestOptions, signal?: AbortSignal) => {
  return request<ScreeningCaseDto>({ url: `/api/screening-cases/${id}`, method: 'GET', signal }, options);
};

export const getScreeningTracingIndividual = (id: string, options?: RequestOptions, signal?: AbortSignal) => {
  return request<ScreeningTracingIndividualDetailsDto>(
    { url: `/api/screening-tracing/individuals/${id}`, method: 'GET', signal },
    options,
  );
};

export const updateScreeningTracingIndividual = (
  id: string,
  updateScreeningTracingIndividualDto: BodyType<UpdateScreeningTracingIndividualDto>,
  options?: RequestOptions,
) => {
  return request<ScreeningTracingIndividualDetailsDto>(
    {
      url: `/api/screening-tracing/individuals/${id}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: updateScreeningTracingIndividualDto,
    },
    options,
  );
};

export const updateScreeningCase = (
  id: string,
  updateScreeningCaseDto: BodyType<UpdateScreeningCaseDto>,
  options?: RequestOptions,
) => {
  return request<ScreeningCaseDto>(
    {
      url: `/api/screening-cases/${id}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: updateScreeningCaseDto,
    },
    options,
  );
};

export const getGetScreeningCaseByIdQueryKey = (id: string) => {
  return [`/api/screening-cases/${id}`] as const;
};

export const getGetScreeningTracingIndividualQueryKey = (id: string) => {
  return [`/api/screening-tracing/individuals/${id}`] as const;
};

export const getGetScreeningCaseByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getScreeningCaseById>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getScreeningCaseById>>, TError, TData>>;
    request?: RequestOptions;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getGetScreeningCaseByIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getScreeningCaseById>>> = ({ signal }) =>
    getScreeningCaseById(id, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getScreeningCaseById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export function useGetScreeningCaseById<
  TData = Awaited<ReturnType<typeof getScreeningCaseById>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getScreeningCaseById>>, TError, TData>>;
    request?: RequestOptions;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetScreeningCaseByIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getGetScreeningCaseByIdSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof getScreeningCaseById>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof getScreeningCaseById>>, TError, TData>>;
    request?: RequestOptions;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getGetScreeningCaseByIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getScreeningCaseById>>> = ({ signal }) =>
    getScreeningCaseById(id, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof getScreeningCaseById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export function useGetScreeningCaseByIdSuspense<
  TData = Awaited<ReturnType<typeof getScreeningCaseById>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<typeof getScreeningCaseById>>, TError, TData>>;
    request?: RequestOptions;
  },
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetScreeningCaseByIdSuspenseQueryOptions(id, options);

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getUpdateScreeningCaseMutationOptions = <
  TData = Awaited<ReturnType<typeof updateScreeningCase>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<UpdateScreeningCaseDto> }, TContext>;
  request?: RequestOptions;
}) => {
  const mutationKey = ['updateScreeningCase'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateScreeningCase>>,
    { id: string; data: BodyType<UpdateScreeningCaseDto> }
  > = ({ id, data }) => updateScreeningCase(id, data, requestOptions);

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<UpdateScreeningCaseDto> },
    TContext
  >;
};

export const useUpdateScreeningCase = <
  TData = Awaited<ReturnType<typeof updateScreeningCase>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<UpdateScreeningCaseDto> }, TContext>;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { id: string; data: BodyType<UpdateScreeningCaseDto> }, TContext> => {
  const mutationOptions = getUpdateScreeningCaseMutationOptions(options);

  return useMutation(mutationOptions);
};

export const getUpdateScreeningTracingIndividualMutationOptions = <
  TData = Awaited<ReturnType<typeof updateScreeningTracingIndividual>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<UpdateScreeningTracingIndividualDto> },
    TContext
  >;
  request?: RequestOptions;
}) => {
  const mutationKey = ['updateScreeningTracingIndividual'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateScreeningTracingIndividual>>,
    { id: string; data: BodyType<UpdateScreeningTracingIndividualDto> }
  > = ({ id, data }) => updateScreeningTracingIndividual(id, data, requestOptions);

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<UpdateScreeningTracingIndividualDto> },
    TContext
  >;
};

export const useUpdateScreeningTracingIndividual = <
  TData = Awaited<ReturnType<typeof updateScreeningTracingIndividual>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<UpdateScreeningTracingIndividualDto> },
    TContext
  >;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { id: string; data: BodyType<UpdateScreeningTracingIndividualDto> }, TContext> => {
  const mutationOptions = getUpdateScreeningTracingIndividualMutationOptions(options);

  return useMutation(mutationOptions);
};

export const getCreateScreeningCaseMutationOptions = <
  TData = Awaited<ReturnType<typeof createScreeningCase>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, BodyType<CreateScreeningCaseDto>, TContext>;
  request?: RequestOptions;
}) => {
  const mutationKey = ['createScreeningCase'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createScreeningCase>>,
    BodyType<CreateScreeningCaseDto>
  > = (data) => createScreeningCase(data, requestOptions);

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    BodyType<CreateScreeningCaseDto>,
    TContext
  >;
};

export const useCreateScreeningCase = <
  TData = Awaited<ReturnType<typeof createScreeningCase>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, BodyType<CreateScreeningCaseDto>, TContext>;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, BodyType<CreateScreeningCaseDto>, TContext> => {
  const mutationOptions = getCreateScreeningCaseMutationOptions(options);

  return useMutation(mutationOptions);
};

// ---------------------------------------------------------------------------
// Tuberculosis History
// ---------------------------------------------------------------------------

export const getScreeningTracingIndividualTuberculosisHistory = (
  id: string,
  options?: RequestOptions,
  signal?: AbortSignal,
) => {
  return request<TuberculosisHistoryDto>(
    { url: `/api/screening-tracing/individuals/${id}/tuberculosis-history`, method: 'GET', signal },
    options,
  );
};

export const updateScreeningTracingIndividualTuberculosisHistory = (
  id: string,
  tuberculosisHistoryDto: BodyType<TuberculosisHistoryDto>,
  options?: RequestOptions,
) => {
  return request<TuberculosisHistoryDto>(
    {
      url: `/api/screening-tracing/individuals/${id}/tuberculosis-history`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tuberculosisHistoryDto,
    },
    options,
  );
};

export const getGetScreeningTracingIndividualTuberculosisHistoryQueryKey = (id: string) => {
  return [`/api/screening-tracing/individuals/${id}/tuberculosis-history`] as const;
};

export const getGetScreeningTracingIndividualTuberculosisHistorySuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof getScreeningTracingIndividualTuberculosisHistory>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof getScreeningTracingIndividualTuberculosisHistory>>,
        TError,
        TData
      >
    >;
    request?: RequestOptions;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getGetScreeningTracingIndividualTuberculosisHistoryQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getScreeningTracingIndividualTuberculosisHistory>>> = ({
    signal,
  }) => getScreeningTracingIndividualTuberculosisHistory(id, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof getScreeningTracingIndividualTuberculosisHistory>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export function useGetScreeningTracingIndividualTuberculosisHistorySuspense<
  TData = Awaited<ReturnType<typeof getScreeningTracingIndividualTuberculosisHistory>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof getScreeningTracingIndividualTuberculosisHistory>>,
        TError,
        TData
      >
    >;
    request?: RequestOptions;
  },
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetScreeningTracingIndividualTuberculosisHistorySuspenseQueryOptions(id, options);

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getUpdateScreeningTracingIndividualTuberculosisHistoryMutationOptions = <
  TData = Awaited<ReturnType<typeof updateScreeningTracingIndividualTuberculosisHistory>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<TuberculosisHistoryDto> }, TContext>;
  request?: RequestOptions;
}) => {
  const mutationKey = ['updateScreeningTracingIndividualTuberculosisHistory'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateScreeningTracingIndividualTuberculosisHistory>>,
    { id: string; data: BodyType<TuberculosisHistoryDto> }
  > = ({ id, data }) => updateScreeningTracingIndividualTuberculosisHistory(id, data, requestOptions);

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<TuberculosisHistoryDto> },
    TContext
  >;
};

export const useUpdateScreeningTracingIndividualTuberculosisHistory = <
  TData = Awaited<ReturnType<typeof updateScreeningTracingIndividualTuberculosisHistory>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TData, TError, { id: string; data: BodyType<TuberculosisHistoryDto> }, TContext>;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { id: string; data: BodyType<TuberculosisHistoryDto> }, TContext> => {
  const mutationOptions = getUpdateScreeningTracingIndividualTuberculosisHistoryMutationOptions(options);

  return useMutation(mutationOptions);
};

// ---------------------------------------------------------------------------
// Screening Cases List API
// ---------------------------------------------------------------------------

export type ClientType = 'BAZ_ZURICH' | 'SELF_REFERRAL' | 'OTHER';

export type DesiredTest = 'MX' | 'QFT' | 'RX';

export type ScreeningCaseStatus = 'OPEN' | 'CLOSED';

export type ScreeningCaseListQuery = {
  activeOnly?: boolean;
  page?: number;
  size?: number;
  search?: string;
};

export type ScreeningCaseListItemDto = {
  screeningCaseId: string;
  patientId: string | null;
  created: string | null;
  requester: string | null;
  clientType: ClientType;
  groupName: string;
  lastName: string | null;
  firstName: string | null;
  testDate: string | null;
  desiredTest: DesiredTest | null;
  status: ScreeningCaseStatus;
  closedAt: string | null;
  individualCount: number;
};

export type ScreeningCaseListResponse = {
  content: ScreeningCaseListItemDto[];
  totalCount: number;
  page: number;
  size: number;
};

export const getScreeningCases = (params?: ScreeningCaseListQuery, options?: RequestOptions, signal?: AbortSignal) => {
  return request<ScreeningCaseListResponse>({ url: `/api/screening-cases`, method: 'GET', params, signal }, options);
};

export const getGetScreeningCasesQueryKey = (params?: ScreeningCaseListQuery) => {
  return [`/api/screening-cases`, ...(params ? [params] : [])] as const;
};

export const getGetScreeningCasesQueryOptions = <
  TData = Awaited<ReturnType<typeof getScreeningCases>>,
  TError = ErrorType<unknown>,
>(
  params?: ScreeningCaseListQuery,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getScreeningCases>>, TError, TData>>;
    request?: RequestOptions;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getGetScreeningCasesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getScreeningCases>>> = ({ signal }) =>
    getScreeningCases(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getScreeningCases>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export function useGetScreeningCases<
  TData = Awaited<ReturnType<typeof getScreeningCases>>,
  TError = ErrorType<unknown>,
>(
  params?: ScreeningCaseListQuery,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getScreeningCases>>, TError, TData>>;
    request?: RequestOptions;
  },
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetScreeningCasesQueryOptions(params, options);

  const query = useQuery({ ...queryOptions, placeholderData: keepPreviousData }) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

// ---------------------------------------------------------------------------
// Preventive Therapy
// ---------------------------------------------------------------------------

export const getScreeningIndividualPreventiveTherapyQueryKey = (id: string) =>
  ['screening-individual-preventive-therapy', id] as const;

export const getScreeningIndividualPreventiveTherapy = (id: string, options?: RequestOptions, signal?: AbortSignal) => {
  return request<PreventiveTherapyDto>(
    { url: `/api/screening-tracing/individuals/${id}/preventive-therapy`, method: 'GET', signal },
    options,
  );
};

export const useGetScreeningIndividualPreventiveTherapy = (id: string) => {
  return useQuery({
    queryKey: getScreeningIndividualPreventiveTherapyQueryKey(id),
    queryFn: ({ signal }) => getScreeningIndividualPreventiveTherapy(id, undefined, signal),
    enabled: !!id,
  });
};

export const updateScreeningIndividualPreventiveTherapy = (
  id: string,
  preventiveTherapyDto: BodyType<PreventiveTherapyDto>,
  options?: RequestOptions,
) => {
  return request<PreventiveTherapyDto>(
    {
      url: `/api/screening-tracing/individuals/${id}/preventive-therapy`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: preventiveTherapyDto,
    },
    options,
  );
};

export const useUpdateScreeningIndividualPreventiveTherapy = () => {
  return useMutation({
    mutationKey: ['updateScreeningIndividualPreventiveTherapy'],
    mutationFn: ({ id, data }: { id: string; data: BodyType<PreventiveTherapyDto> }) =>
      updateScreeningIndividualPreventiveTherapy(id, data),
  });
};

// ---------------------------------------------------------------------------
// Assessment Result
// ---------------------------------------------------------------------------

export enum ScreeningAssessmentResult {
  XRAY_PERFORMED = 'XRAY_PERFORMED',
  INFECTED = 'INFECTED',
  NOT_INFECTED = 'NOT_INFECTED',
  SICK = 'SICK',
  NOT_SHOW_UP = 'NOT_SHOW_UP',
  REFUSED_TEST = 'REFUSED_TEST',
  GONE_HIDING = 'GONE_HIDING',
  STATUS_AFTER_TB = 'STATUS_AFTER_TB',
  STATUS_AFTER_TBI = 'STATUS_AFTER_TBI',
}

export interface SetScreeningAssessmentResultDto {
  result: ScreeningAssessmentResult;
  notes: string | null;
}

export const setScreeningAssessmentResult = (
  id: string,
  data: BodyType<SetScreeningAssessmentResultDto>,
  options?: RequestOptions,
) => {
  return request<void>(
    {
      url: `/api/screening-tracing/individuals/${id}/assessment/result`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data,
    },
    options,
  );
};

export const getSetScreeningAssessmentResultMutationOptions = <
  TData = Awaited<ReturnType<typeof setScreeningAssessmentResult>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<SetScreeningAssessmentResultDto> },
    TContext
  >;
  request?: RequestOptions;
}) => {
  const mutationKey = ['setScreeningAssessmentResult'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setScreeningAssessmentResult>>,
    { id: string; data: BodyType<SetScreeningAssessmentResultDto> }
  > = ({ id, data }) => setScreeningAssessmentResult(id, data, requestOptions);

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<SetScreeningAssessmentResultDto> },
    TContext
  >;
};

export const useSetScreeningAssessmentResult = <
  TData = Awaited<ReturnType<typeof setScreeningAssessmentResult>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<SetScreeningAssessmentResultDto> },
    TContext
  >;
  request?: RequestOptions;
}): UseMutationResult<TData, TError, { id: string; data: BodyType<SetScreeningAssessmentResultDto> }, TContext> => {
  const mutationOptions = getSetScreeningAssessmentResultMutationOptions(options);

  return useMutation(mutationOptions);
};
